import React from 'react'
import { styled } from 'baseui'

/** Exactly one media source — a card with neither would render an empty box. */
type PreviewCardMedia = { videoSrc: string; imageSrc?: never } | { imageSrc: string; videoSrc?: never }

type PreviewCardProps = PreviewCardMedia & {
  /** width / height. Drives the card's width, since the height comes from the row. */
  aspectRatio: number
  /** Overlay badge (premium / super icon). Rendered top-right; omit to show none. */
  badge?: React.ReactNode
  onClick?: () => void
}

const Card = styled('div', (props: { $aspectRatio: number; $clickable: boolean }) => ({
  height: '100%',
  width: 'auto',
  aspectRatio: String(props.$aspectRatio),
  flex: '0 0 auto',
  position: 'relative',
  borderRadius: '16px',
  overflow: 'hidden',
  cursor: props.$clickable ? 'pointer' : 'default',
}))

// Both media elements fill the card and crop rather than letterbox.
const MEDIA_STYLE = { width: '100%', height: '100%', objectFit: 'cover' as const, display: 'block' }

const Video = styled('video', MEDIA_STYLE)

const Image = styled('img', MEDIA_STYLE)

const Badge = styled('div', {
  position: 'absolute',
  top: '8px',
  right: '8px',
  zIndex: 1,
  pointerEvents: 'none',
})

/**
 * A dashboard feed card: one media preview (image or looping video) with an
 * optional corner badge. The card takes its height from the row it sits in
 * (`HorizontalScroller`'s `itemHeight`) and derives its width from
 * `aspectRatio`, so every card in a row lines up on a single baseline.
 */
function PreviewCard({ videoSrc, imageSrc, aspectRatio, badge, onClick }: PreviewCardProps) {
  return (
    <Card $aspectRatio={aspectRatio} $clickable={Boolean(onClick)} onClick={onClick}>
      {videoSrc ? (
        <Video src={videoSrc} autoPlay muted loop playsInline preload="metadata" />
      ) : (
        <Image src={imageSrc} alt="" loading="lazy" />
      )}
      {badge ? <Badge>{badge}</Badge> : null}
    </Card>
  )
}

export default PreviewCard
